import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, LifeBuoy, LocateFixed, MapPin, ShieldCheck, TriangleAlert } from "lucide-react";
import { errorMessage } from "../../api/client";
import { punchApi, type FailureReason, type PunchView } from "../../api/clock";
import { Button, ErrorText, FullScreenLoader } from "../../components/ui";
import { AssistanceForm, BlockedHelp, PunchShell, AccuracyTips } from "./punchParts";

const OUTCOME_TEXT: Record<string, string> = {
  STALE: "Your phone gave us an old location. Tap Try again.",
  INACCURATE: "Your location wasn't precise enough. Step outside or near a window, then try again.",
  OUT_OF_RANGE: "You don't appear to be at the venue yet.",
  GPS_UNAVAILABLE: "Your phone couldn't find your location.",
  GPS_TIMEOUT: "Finding your location took too long.",
  GPS_DENIED: "Location permission was blocked.",
  GPS_UNSUPPORTED: "This browser can't share location.",
  LINK_EXPIRED: "This link expired before your location was confirmed.",
};
const GEO_ERR: Record<number, FailureReason> = { 1: "PERMISSION_DENIED", 2: "POSITION_UNAVAILABLE", 3: "TIMEOUT" };


export default function PunchPage() {
  const { token = "" } = useParams();
  const [view, setView] = useState<PunchView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState("");
  const [phase, setPhase] = useState<"idle" | "locating" | "sending">("idle");
  const [assist, setAssist] = useState(false);

  useEffect(() => {
    punchApi.get(token).then((v) => { setView(v); setSelected(v.selected_assignment_id ?? ""); }).catch((e) => setLoadError(errorMessage(e)));
  }, [token]);

  const run = useCallback(async (fn: () => Promise<PunchView>) => {
    setPhase("sending");
    setError(null);
    try {
      setView(await fn());
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setPhase("idle");
    }
  }, []);

  const share = () => {
    if (!navigator.geolocation) return run(() => punchApi.failure(token, "UNSUPPORTED"));
    setPhase("locating");
    setError(null);
    navigator.geolocation.getCurrentPosition(
      (p) => run(() => punchApi.location(token, {
        latitude: p.coords.latitude, longitude: p.coords.longitude, accuracy_m: p.coords.accuracy,
        position_timestamp: new Date(p.timestamp).toISOString(), ...(selected ? { selected_assignment_id: selected } : {}),
      })),
      (err) => run(() => punchApi.failure(token, GEO_ERR[err.code] ?? "POSITION_UNAVAILABLE")),
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
    );
  };

  if (loadError) return <PunchShell testId="punch-page-invalid"><p data-testid="punch-load-error" className="text-base text-zinc-200">{loadError}</p></PunchShell>;
  if (!view) return <FullScreenLoader />;
  const dir = view.type === "IN" ? "in" : "out";
  const venue = view.candidates.find((c) => c.assignment_id === selected)?.venue_name ?? (view.candidates.length === 1 ? view.candidates[0].venue_name : "your venue");
  const needsPick = view.candidates.length > 1 && !selected;
  const outcome = view.outcome && view.outcome !== "PASS" ? view.outcome : view.last_outcome !== "PASS" ? view.last_outcome : null;

  return (
    <PunchShell testId="punch-page" eyebrow={`Clock-${dir} check`}>
      {view.outcome === "LINK_REUSED" && (
        <p data-testid="punch-link-used" className="rounded-sm border border-white/15 bg-surface p-3 text-sm text-zinc-200">This link was already used{view.consumed_local ? ` (${view.consumed_local})` : ""}. Final result below. Nothing was changed.</p>
      )}
      {view.status === "CONFIRMED" && (
        <div data-testid="punch-confirmed" className="rounded-sm border border-emerald-400/40 bg-emerald-400/10 p-6">
          <CheckCircle2 className="mb-3 h-10 w-10 text-emerald-400" />
          <p className="font-display text-3xl font-bold uppercase text-white">Clocked {view.type}</p>
          <p className="mt-2 text-sm text-zinc-200">{venue}. You'll get a confirmation text with your time. You can close this page.</p>
        </div>
      )}
      {view.status === "EXCEPTION" && (
        <div data-testid="punch-exception" className="rounded-sm border border-warn/50 bg-warn/10 p-6">
          <TriangleAlert className="mb-3 h-8 w-8 text-warn" />
          <p className="font-display text-2xl font-bold uppercase text-white">We couldn't confirm this punch</p>
          <p data-testid="punch-exception-reason" className="mt-2 text-sm text-zinc-200">{OUTCOME_TEXT[view.exception_codes[0]] ?? "An admin will review it."}</p>
          <p className="mt-2 text-sm font-semibold text-white">Your text time is already saved.</p>
          {view.exception_codes.includes("GPS_DENIED") && <BlockedHelp />}
        </div>
      )}
      {view.status === "PENDING_GPS" && (
        <>
          <p data-testid="punch-greeting" className="text-lg text-white">Hi {view.first_name}.</p>
          <p data-testid="punch-explainer" className="text-sm leading-relaxed text-zinc-300">
            We'll check your location <strong className="text-white">ONE time</strong> to confirm this clock-{dir} at <strong className="text-white">{venue}</strong>. We don't track you. Your coordinates and accuracy are stored for timekeeping review and kept for up to 3 years.
          </p>
          {view.candidates.length > 1 ? (
            <fieldset data-testid="punch-candidates" className="space-y-2">
              <legend className="label-caps mb-2 text-zinc-400">Which shift are you clocking {dir} for? (required)</legend>
              {view.candidates.map((c) => (
                <label key={c.assignment_id} className={`flex cursor-pointer items-start gap-3 rounded-sm border p-4 ${selected === c.assignment_id ? "border-signal bg-signal/10" : "border-white/15"}`}>
                  <input type="radio" name="shift" value={c.assignment_id} checked={selected === c.assignment_id} onChange={() => setSelected(c.assignment_id)} className="mt-1 accent-[#ff1d00]" data-testid={`punch-candidate-${c.assignment_id}`} />
                  <span><span className="block font-semibold text-white">{c.venue_name}</span><span className="text-xs text-zinc-400">{c.local_time}{c.role ? ` · ${c.role}` : ""}</span></span>
                </label>
              ))}
            </fieldset>
          ) : (
            <div data-testid="punch-single-shift" className="flex items-center gap-3 rounded-sm border border-white/15 p-4">
              <MapPin className="h-5 w-5 text-signal" />
              <span><span className="block font-semibold text-white">{view.candidates[0]?.venue_name}</span><span className="text-xs text-zinc-400">{view.candidates[0]?.local_time}</span></span>
            </div>
          )}
          {outcome && (
            <div data-testid="punch-attempt-result" className="rounded-sm border border-warn/40 bg-warn/5 p-4 text-sm text-zinc-200">
              <p className="font-semibold text-white">{OUTCOME_TEXT[outcome] ?? outcome}</p>
              <p className="mt-1 text-xs text-zinc-400">Attempt {view.attempts_used} of {view.attempts_max}.{view.result?.distance_m != null ? ` About ${view.result.distance_m} m away.` : ""}</p>
            </div>
          )}
          <Button className="h-16 w-full rounded-[18px] text-base" onClick={share} loading={phase !== "idle"} disabled={needsPick} data-testid="punch-share-location-button">
            <LocateFixed className="h-5 w-5" /> {view.attempts_used ? "Try again" : "Share my location for this punch"}
          </Button>
          {phase === "locating" && <p data-testid="punch-locating" className="text-center text-xs text-zinc-400">Finding your location… keep this page open.</p>}
          <p className="flex items-center gap-2 text-xs text-zinc-500"><ShieldCheck className="h-4 w-4" /> Link expires at {view.expires_local}.</p>
          <AccuracyTips />
        </>
      )}
      <ErrorText message={error} testId="punch-error" />
      {assist || view.assistance_requested ? (
        <AssistanceForm done={view.assistance_requested} onSubmit={(note) => run(() => punchApi.assistance(token, note))} />
      ) : (
        <Button variant="secondary" className="w-full" onClick={() => setAssist(true)} data-testid="punch-request-assistance-button">
          <LifeBuoy className="h-4 w-4" /> Request Assistance / Correction
        </Button>
      )}
      <p className="text-xs text-zinc-500">If this doesn't work, tap Request Assistance. Your text time is already saved.</p>
    </PunchShell>
  );
}
